/**
 * VoiceNoteRecorder — single mic button for the chat input toolbar.
 *
 * States: idle → recording → transcribing → idle.
 *
 * Flow:
 *   1. User clicks idle mic → getUserMedia({audio:true}) → MediaRecorder spins up.
 *   2. While recording: elapsed time updates every 250ms, pulsing red dot.
 *   3. User clicks stop → recorder stops, chunks concat into a Blob.
 *   4. Blob → base64 (FileReader) → POST /api/auth/transcribe.
 *   5. On 200: caller appends transcript (prefixed with 🎤) to message input.
 *   6. On error: inline red text; always return to idle and tear down mic.
 *
 * Critical invariant: MediaStream tracks are stopped on EVERY exit path
 * (success, error, unmount, hard-cap). Leaking the mic indicator is a UX bug.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './VoiceNoteRecorder.module.css';

const TOKEN_KEY = 'foreman-auth-token';

/** Hard cap on recording length. Whisper accepts 25MB; ~5min of opus stays
 *  well under that. Also stops runaway recordings if the user forgets. */
const MAX_RECORDING_MS = 5 * 60 * 1000;

/** Ordered MIME-type preference list. Chrome/Firefox land on opus/webm,
 *  iOS Safari has no webm support → falls back to mp4 (AAC). audio/mpeg is
 *  a final fallback though no current browser actually returns it from
 *  MediaRecorder — kept for forward-compat. */
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
] as const;

type RecorderState = 'idle' | 'recording' | 'transcribing';

interface VoiceNoteRecorderProps {
  activeSessionId: string | null;
  /** Called with the transcript text (already prefixed with 🎤) once Whisper returns. */
  onTranscript: (text: string) => void;
  disabled?: boolean;
}

function pickMimeType(): string {
  // SSR safety + jsdom safety: MediaRecorder may not exist.
  if (typeof MediaRecorder === 'undefined') return '';
  for (const candidate of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(candidate)) return candidate;
    } catch {
      /* some browsers throw on unknown types — skip */
    }
  }
  return '';
}

/** Map a MIME type to a sensible filename extension. */
function extForMime(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('mpeg')) return 'mp3';
  return 'bin';
}

/** Format elapsed ms as "M:SS". */
function formatElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Blob → base64 (no data: prefix). */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result type'));
        return;
      }
      // result is "data:audio/webm;base64,AAAA..." — strip the prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

export function VoiceNoteRecorder({
  activeSessionId,
  onTranscript,
  disabled,
}: VoiceNoteRecorderProps) {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Refs avoid stale-closure problems inside MediaRecorder callbacks and timers.
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);
  const hardCapRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mimeRef = useRef<string>('');
  const mountedRef = useRef(true);

  /** Stop all mic tracks + clear timers. Always safe to call. */
  const cleanupMedia = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (hardCapRef.current) {
      clearTimeout(hardCapRef.current);
      hardCapRef.current = null;
    }
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => {
        try { t.stop(); } catch { /* ignore */ }
      });
      streamRef.current = null;
    }
    recorderRef.current = null;
  }, []);

  // Always tear down the mic on unmount — leaking the OS-level mic
  // indicator is the worst-case UX failure for this component.
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      try {
        const rec = recorderRef.current;
        if (rec && rec.state !== 'inactive') rec.stop();
      } catch { /* ignore */ }
      cleanupMedia();
    };
  }, [cleanupMedia]);

  const uploadAndTranscribe = useCallback(
    async (blob: Blob, mimeType: string) => {
      try {
        const audioBase64 = await blobToBase64(blob);
        const ext = extForMime(mimeType);
        const stamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `voice-note-${stamp}.${ext}`;

        const token = localStorage.getItem(TOKEN_KEY);
        const res = await fetch('/api/auth/transcribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            audioBase64,
            filename,
            mimeType,
            sessionKey: activeSessionId ?? undefined,
          }),
        });

        if (!res.ok) {
          if (res.status === 503) {
            throw new Error('Voice transcription not configured yet — ask Martin.');
          }
          if (res.status === 413) {
            throw new Error('Recording too large (>25MB). Try a shorter note.');
          }
          let detail = `Transcription failed (${res.status})`;
          try {
            const body = await res.json();
            if (body?.error) detail = body.error;
          } catch { /* ignore JSON errors */ }
          throw new Error(detail);
        }

        const json = (await res.json()) as {
          ok?: boolean;
          transcript?: string;
        };
        const transcript = (json.transcript ?? '').trim();
        if (!transcript) {
          throw new Error("Couldn't hear anything — try again.");
        }

        if (mountedRef.current) {
          // Marker prefix lets the Foreman skill recognize voice input.
          onTranscript(`\u{1F3A4} ${transcript}`);
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Transcription failed.';
        if (mountedRef.current) setError(message);
      } finally {
        if (mountedRef.current) {
          setState('idle');
          setElapsedMs(0);
        }
      }
    },
    [activeSessionId, onTranscript],
  );

  const stopRecording = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec) {
      // No active recorder — just reset.
      cleanupMedia();
      setState('idle');
      setElapsedMs(0);
      return;
    }
    try {
      if (rec.state !== 'inactive') {
        rec.stop(); // Triggers onstop → finalize.
      }
    } catch {
      // Force the path anyway.
      cleanupMedia();
      setState('idle');
      setElapsedMs(0);
    }
  }, [cleanupMedia]);

  const startRecording = useCallback(async () => {
    setError(null);

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('Mic not supported in this browser.');
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      setError('Recording not supported in this browser.');
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const e = err as DOMException;
      if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') {
        setError('Mic permission denied. Enable it in browser settings.');
      } else if (e?.name === 'NotFoundError') {
        setError('No microphone found.');
      } else {
        setError('Could not access microphone.');
      }
      return;
    }

    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      // Pass mimeType only if non-empty — some browsers reject empty string.
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch {
      // Last-resort: no opts at all.
      try {
        recorder = new MediaRecorder(stream);
      } catch {
        stream.getTracks().forEach((t) => t.stop());
        setError('Recording not supported in this browser.');
        return;
      }
    }

    // The effective mime can differ from what we asked for (browser quirk),
    // so trust the recorder's own value.
    const effectiveMime = recorder.mimeType || mimeType || 'audio/webm';
    mimeRef.current = effectiveMime;

    chunksRef.current = [];
    streamRef.current = stream;
    recorderRef.current = recorder;

    recorder.ondataavailable = (ev: BlobEvent) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };

    recorder.onerror = () => {
      cleanupMedia();
      if (mountedRef.current) {
        setError('Recorder error — please try again.');
        setState('idle');
        setElapsedMs(0);
      }
    };

    recorder.onstop = () => {
      const usedMime = mimeRef.current || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: usedMime });
      chunksRef.current = [];
      // Tracks must die immediately — don't wait for the upload to finish.
      cleanupMedia();
      if (!mountedRef.current) return;

      if (blob.size === 0) {
        setError("Nothing recorded — hold the mic and try again.");
        setState('idle');
        setElapsedMs(0);
        return;
      }
      setState('transcribing');
      void uploadAndTranscribe(blob, usedMime);
    };

    startedAtRef.current = Date.now();
    setElapsedMs(0);
    setState('recording');
    recorder.start();

    timerRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 250);

    // Hard cap so a forgotten recording can't run forever.
    hardCapRef.current = setTimeout(() => {
      stopRecording();
    }, MAX_RECORDING_MS);
  }, [cleanupMedia, stopRecording, uploadAndTranscribe]);

  const handleClick = () => {
    if (disabled) return;
    if (state === 'idle') {
      void startRecording();
    } else if (state === 'recording') {
      stopRecording();
    }
    // While transcribing: button is disabled, no-op.
  };

  const btnTitle =
    state === 'recording'
      ? 'Stop recording'
      : state === 'transcribing'
      ? 'Transcribing…'
      : 'Record voice note';

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={`${styles.micBtn} ${
          state === 'recording' ? styles.recording : ''
        } ${state === 'transcribing' ? styles.transcribing : ''}`}
        onClick={handleClick}
        disabled={disabled || state === 'transcribing'}
        title={btnTitle}
        aria-label={btnTitle}
        aria-pressed={state === 'recording'}
      >
        {/* 🎤 */}
        {'\u{1F3A4}'}
      </button>

      {state === 'recording' && (
        <span className={styles.statusText} aria-live="polite">
          <span className={styles.dot} aria-hidden />
          {formatElapsed(elapsedMs)}
        </span>
      )}

      {state === 'transcribing' && (
        <span className={styles.statusText} aria-live="polite">
          <span className={styles.spinner} aria-hidden />
          Transcribing…
        </span>
      )}

      {error && state === 'idle' && (
        <span className={styles.error} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
